// ✅ ContractorListing.jsx 
import React, { useState, useContext, useEffect, useMemo } from "react";
import axios from "axios";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";
import HeaderBar from "../../components/HeaderBar";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaPlus,
  FaSearch,
  FaChevronUp,
  FaChevronDown,
  FaTrash
} from "react-icons/fa";
import PaymentLayoutWrapper from "../../components/PaymentLayoutWrapper";
import { useNavigate } from "react-router-dom";

const BASE_URL = "http://nissi-india.com"; // ✅ change if needed

const ContractorListing = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const tableHeaderClass = buttonClass;

  const navigate = useNavigate();

  const [showFilters, setShowFilters] = useState(true);
  const [loading, setLoading] = useState(false);

  // ✅ localStorage helper
  const getLS = (key, fallback = "") => {
    if (typeof window === "undefined") return fallback;
    const val = localStorage.getItem(key);
    return val !== null && val !== undefined ? val : fallback;
  };

  // ✅ localStorage values
  const userIdLS =
    getLS("userId") || getLS("loginuserid") || getLS("loginUserId");
  const branchIdLS = getLS("branchId") || getLS("branchid") || "8";
  const branchTypeLS = getLS("branchType") || getLS("branchtype") || "HO";

  // ✅ parsed values
  const userId = useMemo(() => String(userIdLS || "").trim(), [userIdLS]);
  const branchId = useMemo(() => Number(branchIdLS || 8), [branchIdLS]);
  const branchType = useMemo(
    () => String(branchTypeLS || "HO").trim(),
    [branchTypeLS]
  );

  // Dropdown options
  const [nameOptions, setNameOptions] = useState([]); // [{id,name}]
  const [codeOptions, setCodeOptions] = useState([]); // [{id,code}]

  // Search state
  const [searchData, setSearchData] = useState({
    contractorName: "",
    contractorCode: ""
  });

  // Listing data
  const [contractorData, setContractorData] = useState([]);

  // ✅ Selection (checkbox) — still used for "select all" + bulk Remove
  const [selectedIds, setSelectedIds] = useState([]);

  const handleSearchChange = (field, value) => {
    setSearchData((prev) => ({ ...prev, [field]: value }));
  };

  const clearSelection = () => setSelectedIds([]);

  // ✅ Load dropdowns
  useEffect(() => {
    if (!branchId) {
      toast.error("branchId not found. Please login again.");
      return;
    }

    const loadDropdowns = async () => {
      try {
        const [namesRes, codesRes] = await Promise.all([
          axios.get(`${BASE_URL}/api/contractors/names`, {
            params: { branchid: branchId, branchType, userid: userId }
          }),
          axios.get(`${BASE_URL}/api/contractors/codes`, {
            params: { branchid: branchId, branchType, userid: userId }
          })
        ]);

        setNameOptions(namesRes?.data?.data || []);
        setCodeOptions(codesRes?.data?.data || []);
      } catch (err) {
        console.error(err);
        toast.error("Failed to load contractor dropdowns");
      }
    };

    loadDropdowns();
  }, [branchId, branchType, userId]);

  // ✅ Load list (default full list)
  useEffect(() => {
    if (!branchId) return;
    handleSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId]);

  const handleSearch = async () => {
    if (!branchId) return toast.error("branchId missing");

    setLoading(true);
    try {
      const res = await axios.get(`${BASE_URL}/api/contractors/list`, {
        params: {
          branchid: branchId,
          branchType,
          userid: userId,
          code: searchData.contractorCode || "",
          name: searchData.contractorName || ""
        }
      });

      const rows = res?.data?.data || [];
      setContractorData(rows);
      clearSelection();
      toast.success("Search completed");
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch contractor list");
    } finally {
      setLoading(false);
    }
  };

  const handleAddNew = () => {
    navigate("/PaymentVouchers/ContractorEntry", {
      state: {
        mode: "add",
        branchId,
        branchType,
        userId
      }
    });
  };

  const handleClear = () => {
    setSearchData({ contractorName: "", contractorCode: "" });
    setTimeout(() => handleSearch(), 0);
  };

  // ✅ Checkbox handlers
  const toggleRow = (id) => {
    if (id === null || id === undefined) return;
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const allRowIds = contractorData
    .map((r) => r.contractorMasId)
    .filter((x) => x !== null && x !== undefined);

  const isAllSelected =
    allRowIds.length > 0 && allRowIds.every((id) => selectedIds.includes(id));

  const toggleSelectAll = () => {
    if (isAllSelected) setSelectedIds([]);
    else setSelectedIds(allRowIds);
  };

  // ✅ Delete selected (bulk, via "Remove" button below the table)
  const handleDeleteSelected = async () => {
    if (!branchId) return toast.error("branchId missing");
    if (selectedIds.length === 0) return;

    const ok = window.confirm(
      `Delete ${selectedIds.length} selected contractor(s)?`
    );
    if (!ok) return;

    try {
      setLoading(true);

      const res = await axios.post(`${BASE_URL}/api/contractors/delete`, {
        branchid: branchId,
        branchType,
        userid: userId,
        ids: selectedIds
      });

      if (res?.data?.success) {
        toast.success("Deleted successfully");
        await handleSearch();
      } else {
        toast.error(res?.data?.message || "Delete failed");
      }
    } catch (err) {
      console.error(err);
      toast.error("Delete failed");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Update — same getById -> redirect flow, now triggered per-row from the Edit icon
  const handleUpdateRow = async (contractorMasId) => {
    if (!branchId) return toast.error("branchId missing");
    if (!contractorMasId) return toast.error("Invalid contractor row");

    try {
      setLoading(true);

      const res = await axios.get(`${BASE_URL}/api/contractors/getById`, {
        params: {
          branchid: branchId,
          branchType,
          userid: userId,
          contractorid: contractorMasId
        }
      });

      const row = res?.data?.data;
      if (!res?.data?.success || !row) {
        toast.error(res?.data?.message || "Failed to load contractor");
        return;
      }

      navigate("/PaymentVouchers/ContractorEntry", {
        state: {
          mode: "edit",
          contractor: row,
          branchId,
          branchType,
          userId
        }
      });
    } catch (err) {
      console.error(err);
      toast.error("Failed to open update");
    } finally {
      setLoading(false);
    }
  };

  return (
    <PaymentLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">CONTRACTOR LISTING</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          {/* ==================== HEADER ==================== */}
          <div className="flex justify-between items-center mb-6">
            <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
              Contractor Listing
            </h1>

            <div className="flex items-center gap-3 flex-wrap">
              <button
                onClick={handleAddNew}
                className={`flex items-center gap-2 px-5 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-200 ${buttonClass} ${hoverClass} text-white`}
              >
                <FaPlus />
                Add New
              </button>

              <button
                onClick={() => setShowFilters((s) => !s)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>

          {/* ==================== SEARCH FILTERS ==================== */}
          {showFilters && (
            <div className={`p-6 rounded-lg mb-6 ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`}>
              <div className="flex items-center justify-between mb-4">
                <h3 className={`text-lg font-semibold ${isDark ? "text-white" : "text-blue-900"}`}>
                  Search Criteria
                </h3>

                <div className="flex gap-2">
                  <button
                    onClick={handleClear}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold border ${
                      isDark
                        ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-white"
                    }`}
                  >
                    Clear
                  </button>

                  <button
                    onClick={handleSearch}
                    disabled={loading}
                    className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${buttonClass} ${hoverClass} text-white ${
                      loading ? "opacity-60 cursor-not-allowed" : ""
                    }`}
                  >
                    <FaSearch />
                    {loading ? "Searching..." : "Search"}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Contractor Name
                  </label>
                  <select
                    value={searchData.contractorName}
                    onChange={(e) => handleSearchChange("contractorName", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                    }`}
                  >
                    <option value="">Select</option>
                    {nameOptions.map((x, idx) => (
                      <option key={x.id ?? idx} value={x.name}>
                        {x.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Contractor Code
                  </label>
                  <select
                    value={searchData.contractorCode}
                    onChange={(e) => handleSearchChange("contractorCode", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                    }`}
                  >
                    <option value="">Select</option>
                    {codeOptions.map((x, idx) => (
                      <option key={x.id ?? idx} value={x.code}>
                        {x.code}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* ==================== RESULTS TABLE ==================== */}
          <div className="overflow-x-auto rounded-lg border shadow-lg">
            <table className={`w-full ${isDark ? "bg-gray-800" : "bg-white"}`}>
              <thead className={`${tableHeaderClass} text-white`}>
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Sl.No</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Contractor Id</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Contractor Name</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Contact Number</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Address</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Branch Name</th>

                  {/* EDIT - SECOND LAST COLUMN */}
                  <th className="px-6 py-4 text-center text-sm font-semibold">Edit</th>

                  {/* SELECT ALL - LAST COLUMN */}
                  <th className="px-6 py-4 text-center text-sm font-semibold w-16">
                    <input
                      type="checkbox"
                      checked={isAllSelected}
                      onChange={toggleSelectAll}
                      className="w-4 h-4 cursor-pointer"
                      title="Select All"
                    />
                  </th>
                </tr>
              </thead>

              <tbody className={`${isDark ? "divide-gray-700" : "divide-gray-200"} divide-y`}>
                {loading ? (
                  <tr>
                    <td colSpan={8} className={`px-6 py-10 text-center ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                      Loading...
                    </td>
                  </tr>
                ) : contractorData.length === 0 ? (
                  <tr>
                    <td colSpan={8} className={`px-6 py-10 text-center ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                      No data found. Please search.
                    </td>
                  </tr>
                ) : (
                  contractorData.map((c, idx) => {
                    const id = c.contractorMasId;
                    const checked = selectedIds.includes(id);

                    return (
                      <tr
                        key={`${id || idx}-${idx}`}
                        className={`${
                          idx % 2 === 0
                            ? isDark ? "bg-gray-800" : "bg-white"
                            : isDark ? "bg-gray-750" : "bg-gray-50"
                        } transition-colors ${isDark ? "hover:bg-gray-700" : "hover:bg-blue-50"}`}
                      >
                        <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {c.slNo ?? idx + 1}
                        </td>
                        <td className={`px-6 py-4 text-sm font-medium ${isDark ? "text-blue-400" : "text-blue-600"}`}>
                          {c.contractorId}
                        </td>
                        <td className={`px-6 py-4 text-sm font-medium ${isDark ? "text-white" : "text-gray-900"}`}>
                          {c.contractorName}
                        </td>
                        <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {c.contactNumber}
                        </td>
                        <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {c.address}
                        </td>
                        <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {c.branchName}
                        </td>

                        {/* EDIT */}
                        <td className="px-6 py-4 text-center">
                          <button
                            type="button"
                            onClick={() => handleUpdateRow(id)}
                            className="text-yellow-500 hover:text-yellow-600 text-lg transition"
                            title="Edit"
                          >
                            ✏️
                          </button>
                        </td>

                        {/* SELECT */}
                        <td className="px-6 py-4 text-center">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleRow(id)}
                            className="w-4 h-4 cursor-pointer"
                          />
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>

            {/* ==================== REMOVE BUTTON ==================== */}
            <div className="p-4">
              <button
                onClick={handleDeleteSelected}
                disabled={selectedIds.length === 0}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold shadow transition ${
                  selectedIds.length === 0 ? "opacity-50 cursor-not-allowed" : ""
                } bg-red-600 hover:bg-red-700 text-white`}
              >
               🗑
                Remove
              </button>
            </div>
          </div>

          {/* ==================== TOAST ==================== */}
          <ToastContainer
            position="top-right"
            autoClose={3000}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </PaymentLayoutWrapper>
  );
};

export default ContractorListing;